import React, { useState, useEffect } from 'react';
import { 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Switch, 
  FormControlLabel, 
  CircularProgress, 
  Alert, 
  Grid, 
  List, 
  ListItem, 
  ListItemText, 
  IconButton, 
  InputAdornment, 
  Tabs, 
  Tab, 
  AppBar, 
  Toolbar, 
  Select, 
  InputLabel, 
  FormControl, 
  SelectChangeEvent, 
  ToggleButton, 
  ToggleButtonGroup, 
  Container,
  Card,
  CardContent,
  Stack,
  Divider,
  useTheme,
  useMediaQuery,
  MenuItem
} from '@mui/material';
import { Site, SiteSettings } from '../types/Site';
import axios from 'axios';
import { useUser } from '../contexts/UserContext';
import { useNavigate, useParams } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import SettingsIcon from '@mui/icons-material/Settings';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import BlockIcon from '@mui/icons-material/Block';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import WarningIcon from '@mui/icons-material/Warning';
import FormatColorFillIcon from '@mui/icons-material/FormatColorFill';
import GradientIcon from '@mui/icons-material/Gradient';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import SaveIcon from '@mui/icons-material/Save';
import {
  SportsTennis,
  SportsBasketball,
  SportsSoccer,
  SportsVolleyball,
  SportsBaseball,
  SportsRugby,
  SportsGolf,
  DirectionsRun,
  FitnessCenter,
  Pool
} from '@mui/icons-material';
import WhatsAppUsageManager from './WhatsAppUsageManager';
import SiteAdminManagement from './SiteAdminManagement';
import DangerZone from './siteManagement/DangerZone';
import CampaignIcon from '@mui/icons-material/Campaign';

const ICON_OPTIONS = [
  { value: 'SportsTennis', label: '網球 / 羽毛球', icon: <SportsTennis /> },
  { value: 'SportsBasketball', label: '籃球', icon: <SportsBasketball /> },
  { value: 'SportsSoccer', label: '足球', icon: <SportsSoccer /> },
  { value: 'SportsVolleyball', label: '排球', icon: <SportsVolleyball /> },
  { value: 'SportsBaseball', label: '棒球', icon: <SportsBaseball /> },
  { value: 'SportsRugby', label: '欖球', icon: <SportsRugby /> },
  { value: 'SportsGolf', label: '高爾夫球', icon: <SportsGolf /> },
  { value: 'DirectionsRun', label: '跑步', icon: <DirectionsRun /> },
  { value: 'FitnessCenter', label: '健身', icon: <FitnessCenter /> },
  { value: 'Pool', label: '桌球', icon: <Pool /> },
];

interface BlacklistEntry {
  phone: string;
  reason: string;
  addedAt: Date;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`site-tabpanel-${index}`}
      aria-labelledby={`site-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `site-tab-${index}`,
    'aria-controls': `site-tabpanel-${index}`,
  };
}

const SiteManagement: React.FC = () => {
  const [site, setSite] = useState<Site | null>(null);
  const [name, setName] = useState('');
  const [settings, setSettings] = useState<SiteSettings>({
    defaultWhatsappTemplate: '',
    defaultSenderId: '',
    defaultSenderName: '',
    isSendWhatsappByDefault: false,
    requireApproval: false,
    whatsappMessageTemplate: '',
    paymentLink: '',
    icon: 'SportsTennis',
    heroBackground: 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)',
    whatsappLimit: {
      dailyLimit: 20,
      monthlyLimit: 600,
      isEnabled: true
    }
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const { isAdmin, loading } = useUser();
  const navigate = useNavigate();
  const { route } = useParams<{ route: string }>();
  const [blacklistPhone, setBlacklistPhone] = useState('');
  const [blacklistReason, setBlacklistReason] = useState('');
  const [blacklist, setBlacklist] = useState<BlacklistEntry[]>([]);
  const [activeTab, setActiveTab] = useState(0);

  // Announcement state
  const [announcementTitle, setAnnouncementTitle] = useState('');
  const [announcementMessage, setAnnouncementMessage] = useState('');
  const [announcementIsImportant, setAnnouncementIsImportant] = useState(false);
  
  // Background settings state
  const [bgType, setBgType] = useState<'solid' | 'gradient'>('gradient');
  const [color1, setColor1] = useState('#1a237e');
  const [color2, setColor2] = useState('#0d47a1');

  // Icon settings state
  const [iconType, setIconType] = useState<'preset' | 'emoji'>('preset');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Light/Dark background detection state
  const [isLightBackground, setIsLightBackground] = useState(false);

  // Helper to determine if a background color is light
  const checkIsLightBackground = (background: string) => {
    // Default to false (dark background / white text)
    let isLight = false;

    // Handle solid hex colors
    if (background.startsWith('#')) {
      const hex = background.replace('#', '');
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      // Calculate brightness
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      isLight = brightness > 128;
    } 
    // Handle linear-gradients (approximate by checking the first color if it's hex)
    else if (background.includes('linear-gradient')) {
      const match = background.match(/#[a-fA-F0-9]{6}/);
      if (match) {
        const hex = match[0].replace('#', '');
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);
        const brightness = (r * 299 + g * 587 + b * 114) / 1000;
        isLight = brightness > 150; // Slightly higher threshold for gradients
      }
    }

    setIsLightBackground(isLight);
  };

  useEffect(() => {
    const fetchSite = async () => {
      if (!route) {
        setError('找不到群組路由');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        // Use the route parameter to get the specific site
        const response = await axios.get(`${API_BASE_URL}/sites/${route}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
        const siteData = response.data;
        
        // Check if user has admin access to this site
        if (siteData.isOwner && siteData.settings) {
          setSite(siteData);
          setName(siteData.name);
          setSettings(siteData.settings);
          setBlacklist(siteData.blacklist || []);
          setIsOwner(true);

          // Determine icon type
          const currentIcon = siteData.settings.icon || 'SportsTennis';
          const isPreset = ICON_OPTIONS.some(opt => opt.value === currentIcon);
          setIconType(isPreset ? 'preset' : 'emoji');

          // Parse hero background setting
          const bg = siteData.settings.heroBackground || 'linear-gradient(135deg, #1a237e 0%, #0d47a1 100%)';
          if (bg.includes('linear-gradient')) {
            setBgType('gradient');
            const colors = bg.match(/#[a-fA-F0-9]{6}/g);
            if (colors && colors.length >= 2) {
              setColor1(colors[0]);
              setColor2(colors[1]);
            }
          } else {
            setBgType('solid');
            setColor1(bg); // Assume it's a color code
          }
        } else {
          setError('沒有權限管理此群組');
          setIsOwner(false);
        }
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 403) {
          setError('沒有權限管理此群組');
          setIsOwner(false);
        } else if (axios.isAxiosError(err) && err.response?.status === 404) {
          setError('找不到群組');
          setIsOwner(false);
        } else {
          setError('載入群組資料時發生錯誤');
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    if (!loading) {
      if (isAdmin) {
        fetchSite();
      } else {
        navigate('/login');
      }
    }
  }, [isAdmin, loading, navigate, route]);

  const handleSettingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = event.target;
    
    // Prevent changing defaultSenderId (phone number)
    if (name === 'defaultSenderId') {
      return;
    }
    
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleIconTypeChange = (event: React.MouseEvent<HTMLElement>, newType: 'preset' | 'emoji' | null) => {
    if (newType !== null) {
      setIconType(newType);
      // If switching to preset and current icon is not in options, reset to default
      if (newType === 'preset') {
        const isCurrentPreset = ICON_OPTIONS.some(opt => opt.value === settings.icon);
        if (!isCurrentPreset) {
          setSettings(prev => ({ ...prev, icon: 'SportsTennis' }));
        }
      } else {
        // If switching to emoji, if current is a preset, default to an emoji based on it or a default one
        if (ICON_OPTIONS.some(opt => opt.value === settings.icon)) {
           let defaultEmoji = '🏆';
           if (settings.icon === 'SportsTennis') defaultEmoji = '🎾';
           else if (settings.icon === 'SportsBasketball') defaultEmoji = '🏀';
           else if (settings.icon === 'SportsSoccer') defaultEmoji = '⚽';
           else if (settings.icon === 'SportsVolleyball') defaultEmoji = '🏐';
           else if (settings.icon === 'SportsBaseball') defaultEmoji = '⚾';
           else if (settings.icon === 'SportsRugby') defaultEmoji = '🏉';
           else if (settings.icon === 'SportsGolf') defaultEmoji = '⛳';
           else if (settings.icon === 'DirectionsRun') defaultEmoji = '🏃';
           else if (settings.icon === 'FitnessCenter') defaultEmoji = '🏋️';
           else if (settings.icon === 'Pool') defaultEmoji = '🎱';
           setSettings(prev => ({ ...prev, icon: defaultEmoji }));
        }
      }
    }
  };

  const handleIconChange = (event: SelectChangeEvent) => {
    setSettings(prev => ({
      ...prev,
      icon: event.target.value as string
    }));
  };

  const handleEmojiChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSettings(prev => ({
      ...prev,
      icon: event.target.value
    }));
  };

  // Update background setting when color pickers change
  useEffect(() => {
    let newBg = '';
    if (bgType === 'solid') {
      newBg = color1;
    } else {
      newBg = `linear-gradient(135deg, ${color1} 0%, ${color2} 100%)`;
    }
    
    // Only update if different to avoid loops
    if (newBg !== settings.heroBackground) {
      setSettings(prev => ({
        ...prev,
        heroBackground: newBg
      }));
    }
  }, [bgType, color1, color2]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check brightness when heroBackground changes
  useEffect(() => {
    if (settings.heroBackground) {
      checkIsLightBackground(settings.heroBackground);
    }
  }, [settings.heroBackground]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isOwner) {
      setError('沒有權限更新此群組');
      return;
    }

    try {
      setError('');
      setSuccess('');
      setIsLoading(true);

      const data = {
        name,
        settings
      };

      if (site) {
        // Update existing site
        await axios.put(
          `${API_BASE_URL}/sites/${site._id}`,
          data,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        setSuccess('群組設定已更新');
      } else {
        // Create new site
        await axios.post(
          `${API_BASE_URL}/sites`,
          data,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        setSuccess('群組已創建');
      }
    } catch (err) {
      if (axios.isAxiosError(err)) {
        setError(err.response?.data.message || '更新群組時發生錯誤');
      } else {
        setError('更新群組時發生錯誤');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddToBlacklist = async () => {
    try {
      if (!site?._id) return;
      const response = await axios.post(
        `${API_BASE_URL}/sites/blacklist`,
        { phone: blacklistPhone, reason: blacklistReason, siteId: site._id },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      setBlacklist(response.data.blacklist);
      setBlacklistPhone('');
      setBlacklistReason('');
      setSuccess('已加入黑名單');
    } catch (err) {
      setError('加入黑名單失敗');
    }
  };

  const handleRemoveFromBlacklist = async (phone: string) => {
    try {
      if (!site?._id) return;
      const response = await axios.delete(
        `${API_BASE_URL}/sites/blacklist/${phone}`,
        { 
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          params: { siteId: site._id }
        }
      );
      setBlacklist(response.data.blacklist);
      setSuccess('已從黑名單移除');
    } catch (err) {
      setError('從黑名單移除失敗');
    }
  };

  const handleCreateAnnouncement = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!site?._id) return;

    try {
      setIsLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/sites/${site._id}/announcements`,
        {
          title: announcementTitle,
          message: announcementMessage,
          isImportant: announcementIsImportant,
          visibility: 'public'
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setSite(prev => prev ? {
        ...prev,
        announcements: [...(prev.announcements || []), response.data.announcement]
      } : null);

      setAnnouncementTitle('');
      setAnnouncementMessage('');
      setAnnouncementIsImportant(false);
      setSuccess('公告已發布');
    } catch (err) {
      console.error(err);
      setError('發布公告失敗');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAnnouncement = async (announcementId: string) => {
    if (!site?._id) return;

    try {
      setIsLoading(true);
      await axios.delete(
        `${API_BASE_URL}/sites/${site._id}/announcements/${announcementId}`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      setSite(prev => prev ? {
        ...prev,
        announcements: (prev.announcements || []).filter(a => a._id !== announcementId)
      } : null);

      setSuccess('公告已刪除');
    } catch (err) {
      console.error(err);
      setError('刪除公告失敗');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const renderBasicSettingsTab = () => (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={isMobile ? 2 : 3} pb={isMobile ? 8 : 0}>
        <Grid container rowSpacing={isMobile ? 2 : 3} columnSpacing={isMobile ? 0 : 3}>
          {/* Site Appearance Section */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SportsEsportsIcon color="primary" /> 群組外觀
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <TextField
                  fullWidth
                  label="群組名稱"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  margin="normal"
                  disabled={isLoading}
                  required
                />

                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>群組圖標</Typography>
                  <ToggleButtonGroup
                    value={iconType}
                    exclusive
                    onChange={handleIconTypeChange}
                    aria-label="icon type"
                    size="small"
                    sx={{ mb: 2, width: '100%' }}
                  >
                    <ToggleButton value="preset" sx={{ flex: 1 }}>預設圖標</ToggleButton>
                    <ToggleButton value="emoji" sx={{ flex: 1 }}>Emoji / 文字</ToggleButton>
                  </ToggleButtonGroup>

                  {iconType === 'preset' ? (
                    <FormControl fullWidth size="small">
                      <InputLabel id="icon-select-label">選擇圖標</InputLabel>
                      <Select
                        labelId="icon-select-label"
                        value={ICON_OPTIONS.some(opt => opt.value === settings.icon) ? settings.icon : 'SportsTennis'}
                        label="選擇圖標"
                        onChange={handleIconChange}
                        disabled={isLoading}
                        renderValue={(selected) => {
                          const option = ICON_OPTIONS.find(opt => opt.value === selected);
                          return (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {option?.icon}
                              {option?.label}
                            </Box>
                          );
                        }}
                      >
                        {ICON_OPTIONS.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {option.icon}
                              <Typography>{option.label}</Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : (
                    <TextField
                      fullWidth
                      size="small"
                      label="輸入 Emoji"
                      value={settings.icon}
                      onChange={handleEmojiChange}
                      disabled={isLoading}
                      placeholder="例如: 🏸"
                    />
                  )}
                </Box>

                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>頁首背景</Typography>
                  <ToggleButtonGroup
                    value={bgType}
                    exclusive
                    onChange={(_, newType) => newType && setBgType(newType)}
                    aria-label="background type"
                    size="small"
                    sx={{ mb: 2, width: '100%' }}
                  >
                    <ToggleButton value="solid" sx={{ flex: 1 }}><FormatColorFillIcon sx={{ mr: 1, fontSize: 18 }} /> 單色</ToggleButton>
                    <ToggleButton value="gradient" sx={{ flex: 1 }}><GradientIcon sx={{ mr: 1, fontSize: 18 }} /> 漸變色</ToggleButton>
                  </ToggleButtonGroup>

                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={bgType === 'gradient' ? 6 : 12}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {bgType === 'gradient' ? '開始顏色' : '選擇顏色'}
                        </Typography>
                        <input
                          type="color"
                          value={color1}
                          onChange={(e) => setColor1(e.target.value)}
                          style={{ width: '100%', height: 40, border: '1px solid #ddd', borderRadius: 4, cursor: 'pointer', padding: 2 }}
                        />
                      </Box>
                    </Grid>
                    
                    {bgType === 'gradient' && (
                      <Grid item xs={6}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">結束顏色</Typography>
                          <input
                            type="color"
                            value={color2}
                            onChange={(e) => setColor2(e.target.value)}
                            style={{ width: '100%', height: 40, border: '1px solid #ddd', borderRadius: 4, cursor: 'pointer', padding: 2 }}
                          />
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                  <Box sx={{ mt: 2, borderRadius: 2, overflow: 'hidden', boxShadow: 1 }}>
                    <Box 
                      sx={{ 
                        height: 60, 
                        width: '100%', 
                        background: settings.heroBackground,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: isLightBackground ? 'text.primary' : 'white',
                        fontWeight: 'bold',
                        textShadow: isLightBackground ? 'none' : '0 1px 2px rgba(0,0,0,0.3)'
                      }}
                    >
                      預覽效果
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Function Settings Section */}
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SettingsIcon color="primary" /> 功能設定
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="發送者名稱"
                    name="defaultSenderName"
                    value={settings.defaultSenderName}
                    onChange={handleSettingsChange}
                    disabled={isLoading}
                    size="small"
                  />

                  <TextField
                    fullWidth
                    label="付款連結"
                    name="paymentLink"
                    value={settings.paymentLink || ''}
                    onChange={handleSettingsChange}
                    disabled={isLoading}
                    placeholder="https://payme.hsbc/..."
                    size="small"
                    helperText="設定後可發送付款連結給參加者"
                  />

                  <TextField
                    fullWidth
                    label="聯絡電話 (唯讀)"
                    name="defaultSenderId"
                    value={settings.defaultSenderId}
                    disabled={true}
                    size="small"
                    InputProps={{
                      startAdornment: <InputAdornment position="start">+852</InputAdornment>,
                    }}
                  />

                  <Box sx={{ bgcolor: 'action.hover', p: 2, borderRadius: 1 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.requireApproval}
                          onChange={handleSettingsChange}
                          name="requireApproval"
                          color="primary"
                        />
                      }
                      label="需要核准首次參加者"
                    />
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4 }}>
                      開啟後，新用戶報名需要管理員手動核准
                    </Typography>
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.isSendWhatsappByDefault}
                          onChange={handleSettingsChange}
                          name="isSendWhatsappByDefault"
                          color="primary"
                          sx={{ mt: 1 }}
                        />
                      }
                      label="預設發送 WhatsApp"
                    />
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* WhatsApp Template Section */}
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WhatsAppIcon color="success" /> WhatsApp 訊息模板
                </Typography>
                <Divider sx={{ mb: 2 }} />
                
                <TextField
                  fullWidth
                  label="自定義訊息內容"
                  name="whatsappMessageTemplate"
                  value={settings.whatsappMessageTemplate || ''}
                  onChange={handleSettingsChange}
                  disabled={isLoading}
                  multiline
                  rows={4}
                  helperText="可用變數: {date}, {dayOfWeek}, {time}, {location}, {details}, {maxParticipants}, {fee}, {route}"
                />
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={isMobile ? {
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            p: 2,
            bgcolor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            boxShadow: 4,
            zIndex: 1000
          } : { 
            display: 'flex', 
            justifyContent: 'flex-end', 
            pt: 2 
          }}>
          <Button 
            type="submit" 
            variant="contained" 
            size="large"
            fullWidth={isMobile}
            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            disabled={isLoading || !isOwner}
            sx={{ minWidth: 150, borderRadius: 8 }}
          >
            {site ? '儲存設定' : '創建群組'}
          </Button>
        </Box>
      </Stack>
    </Box>
  );

  const renderWhatsAppTab = () => (
    <Box sx={{ py: 2, maxWidth: 800, mx: 'auto' }}>
      <WhatsAppUsageManager />
    </Box>
  );

  const renderBlacklistTab = () => (
    <Box sx={{ py: 2, maxWidth: 800, mx: 'auto' }}>
      <Card variant="outlined">
        <CardContent sx={{ '&:last-child': { pb: 2 } }}>
          <Typography variant="h6" gutterBottom>黑名單管理</Typography>
          <Divider sx={{ mb: 3 }} />
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="電話號碼"
                value={blacklistPhone}
                onChange={(e) => setBlacklistPhone(e.target.value.replace(/\D/g, '').slice(0, 8))}
                helperText="請輸入8位數香港電話號碼"
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="原因"
                value={blacklistReason}
                onChange={(e) => setBlacklistReason(e.target.value)}
                size="small"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleAddToBlacklist}
                disabled={!/^[2-9]\d{7}$/.test(blacklistPhone)}
                color="error"
              >
                加入
              </Button>
            </Grid>
          </Grid>

          <List>
            {blacklist.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                目前沒有黑名單記錄
              </Typography>
            ) : (
              blacklist.map((entry) => (
                <ListItem
                  key={entry.phone}
                  divider
                  secondaryAction={
                    <IconButton edge="end" onClick={() => handleRemoveFromBlacklist(entry.phone)}>
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={entry.phone}
                    secondary={
                      <React.Fragment>
                        <Typography component="span" variant="body2" color="text.primary">
                          {entry.reason}
                        </Typography>
                        {` — ${new Date(entry.addedAt).toLocaleDateString()}`}
                      </React.Fragment>
                    }
                  />
                </ListItem>
              ))
            )}
          </List>
        </CardContent>
      </Card>
    </Box>
  );

  const renderAdminTab = () => (
    <Box sx={{ py: 2, maxWidth: 800, mx: 'auto' }}>
      <SiteAdminManagement 
        siteId={site?._id}
        siteName={site?.name}
        userPermissions={site?.permissions}
      />
    </Box>
  );

  const renderAnnouncementsTab = () => (
    <Box sx={{ py: 2, maxWidth: 800, mx: 'auto' }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <CampaignIcon color="primary" /> 發布公告
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                所有全站公告皆對所有人可見。
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Box component="form" onSubmit={handleCreateAnnouncement} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="標題"
                  fullWidth
                  required
                  value={announcementTitle}
                  onChange={(e) => setAnnouncementTitle(e.target.value)}
                  size="small"
                />
                <TextField
                  label="內容"
                  fullWidth
                  required
                  multiline
                  rows={4}
                  value={announcementMessage}
                  onChange={(e) => setAnnouncementMessage(e.target.value)}
                  size="small"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={announcementIsImportant}
                      onChange={(e) => setAnnouncementIsImportant(e.target.checked)}
                      color="error"
                    />
                  }
                  label="重要公告"
                />
                <Button 
                  type="submit" 
                  variant="contained" 
                  fullWidth 
                  startIcon={<SaveIcon />}
                  disabled={isLoading}
                >
                  發布
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>現有公告</Typography>
              <Divider sx={{ mb: 2 }} />
              
              {!site?.announcements || site.announcements.length === 0 ? (
                <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                  目前沒有公告
                </Typography>
              ) : (
                <List>
                  {site.announcements.map((announcement) => (
                    <ListItem
                      key={announcement._id}
                      divider
                      alignItems="flex-start"
                      secondaryAction={
                        <IconButton 
                          edge="end" 
                          aria-label="delete"
                          onClick={() => handleDeleteAnnouncement(announcement._id)}
                          disabled={isLoading}
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                      sx={{ 
                        bgcolor: announcement.isImportant ? 'error.lighter' : 'transparent',
                        borderRadius: 1,
                        mb: 1
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {announcement.isImportant && (
                              <WarningIcon color="error" fontSize="small" />
                            )}
                            <Typography variant="subtitle1" fontWeight="bold">
                              {announcement.title}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <React.Fragment>
                            <Typography variant="body2" color="text.primary" sx={{ my: 0.5, whiteSpace: 'pre-wrap' }}>
                              {announcement.message}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(announcement.createdAt).toLocaleDateString()}
                            </Typography>
                          </React.Fragment>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );

  if (isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100dvh',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <CircularProgress size={60} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          載入中...
        </Typography>
      </Box>
    );
  }

  if (!isOwner && !isLoading) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        沒有權限管理此群組
      </Alert>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      <AppBar position="sticky" color="default" elevation={0} sx={{ borderBottom: '1px solid #e0e0e0', bgcolor: 'white' }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate(`/${route}`)}
            aria-label="返回"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
            群組管理
          </Typography>
        </Toolbar>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons={false}
          allowScrollButtonsMobile
          indicatorColor="primary"
          textColor="primary"
          sx={{ px: 2 }}
        >
          <Tab icon={<SettingsIcon />} iconPosition="start" label="基本設定" {...a11yProps(0)} />
          <Tab icon={<CampaignIcon />} iconPosition="start" label="全站公告" {...a11yProps(1)} />
          {site?.permissions?.canManageAdmins && (
            <Tab icon={<SupervisorAccountIcon />} iconPosition="start" label="管理員" {...a11yProps(2)} />
          )}
          <Tab icon={<WhatsAppIcon />} iconPosition="start" label="WhatsApp" {...a11yProps(site?.permissions?.canManageAdmins ? 3 : 2)} />
          <Tab icon={<BlockIcon />} iconPosition="start" label="黑名單" {...a11yProps(site?.permissions?.canManageAdmins ? 4 : 3)} />

          {site?.role === 'owner' && (
            <Tab icon={<WarningIcon />} iconPosition="start" label="危險區域" {...a11yProps(site?.permissions?.canManageAdmins ? 5 : 4)} sx={{ color: 'error.main' }} />
          )}
        </Tabs>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 2 : 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        <TabPanel value={activeTab} index={0}>
          {renderBasicSettingsTab()}
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {renderAnnouncementsTab()}
        </TabPanel>
        
        {site?.permissions?.canManageAdmins && (
          <TabPanel value={activeTab} index={2}>
            {renderAdminTab()}
          </TabPanel>
        )}

        <TabPanel value={activeTab} index={site?.permissions?.canManageAdmins ? 3 : 2}>
          {renderWhatsAppTab()}
        </TabPanel>
        
        <TabPanel value={activeTab} index={site?.permissions?.canManageAdmins ? 4 : 3}>
          {renderBlacklistTab()}
        </TabPanel>
        
        {site?.role === 'owner' && (
          <TabPanel value={activeTab} index={site?.permissions?.canManageAdmins ? 5 : 4}>
            <DangerZone 
              siteId={site._id} 
              siteName={site.name} 
              siteRoute={site.route} 
            />
          </TabPanel>
        )}
      </Container>
    </Box>
  );
};

export default SiteManagement;
